import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, ExternalLink, ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { amplifyApi } from '../services/amplifyApi';

// Google AdSense component
const AdUnit = ({ adUnitId, publisherId }) => {
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);

  useEffect(() => {
    if (!adUnitId || !publisherId) return;

    // Load AdSense script if not already present
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const script = document.createElement('script');
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => setAdLoaded(true);
      script.onerror = () => console.log('AdSense script failed to load (ad blocker?)');
      document.head.appendChild(script);
    } else {
      setAdLoaded(true);
    }
  }, [adUnitId, publisherId]);

  useEffect(() => {
    if (!adLoaded || !adRef.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      // AdSense may throw if ad blocker is active
    }
  }, [adLoaded]);

  if (!adUnitId || !publisherId) return null;

  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 min-h-[90px]">
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={publisherId}
        data-ad-slot={adUnitId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};

// Promo card component
const PromoCard = ({ promo, brand, onTrackClick }) => {
  const handleClick = () => {
    if (onTrackClick) onTrackClick(promo.configId, 'click');
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4 p-4">
        {promo.imageUrl && (
          <img
            src={promo.imageUrl}
            alt={promo.title}
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 text-sm truncate">{promo.title}</h3>
          {promo.subtitle && (
            <p className="text-slate-500 text-xs mt-1 line-clamp-2">{promo.subtitle}</p>
          )}
        </div>
        {promo.ctaUrl && (
          <a
            href={promo.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors"
            style={{ backgroundColor: brand.colors.primary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = brand.colors.primaryHover || brand.colors.primary}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = brand.colors.primary}
          >
            {promo.ctaText || 'Learn More'}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};

// Cross-sell product card
const CrossSellCard = ({ product, brand }) => {
  return (
    <a
      href={`/products/${product.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-all hover:border-slate-300"
    >
      <div className="aspect-square bg-slate-50 overflow-hidden">
        <img
          src={product.mockupUrl || product.mainImage}
          alt={product.title}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-2.5">
        <p className="text-xs font-medium text-slate-700 truncate">{product.title}</p>
        {product.price > 0 && (
          <p className="text-xs font-bold mt-0.5" style={{ color: brand.colors.primary }}>
            ${product.price.toFixed(2)}
          </p>
        )}
      </div>
    </a>
  );
};

const CreationWaitScreen = ({ creationProgress, totalProducts, completedProducts }) => {
  const brand = useBrand();
  const [monetizationData, setMonetizationData] = useState(null);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const impressionTracked = useRef(new Set());
  const rotationTimer = useRef(null);

  const isMonetizationEnabled = brand?.features?.monetization?.enabled !== false;
  const showPromos = brand?.features?.monetization?.showPromos !== false;
  const showAds = brand?.features?.monetization?.showAds !== false;
  const showCrossSell = brand?.features?.monetization?.showCrossSell !== false;

  // Fetch monetization content on mount
  useEffect(() => {
    if (!isMonetizationEnabled) {
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        const response = await amplifyApi.getMonetizationContent();
        if (response.success && response.enabled) {
          setMonetizationData(response);
        }
      } catch (err) {
        console.error('Error loading monetization content:', err);
      }
      setLoading(false);
    };

    fetchContent();
  }, [isMonetizationEnabled]);

  // Auto-rotate promos every 8 seconds
  useEffect(() => {
    const promos = monetizationData?.promos;
    if (!promos || promos.length <= 1) return;

    rotationTimer.current = setInterval(() => {
      setCurrentPromoIndex(prev => (prev + 1) % promos.length);
    }, 8000);

    return () => {
      if (rotationTimer.current) clearInterval(rotationTimer.current);
    };
  }, [monetizationData?.promos]);

  // Track impressions
  const trackImpression = useCallback((promoId) => {
    if (impressionTracked.current.has(promoId)) return;
    impressionTracked.current.add(promoId);
    amplifyApi.trackMonetizationEvent(promoId, 'impression');
  }, []);

  // Track impression for current promo
  useEffect(() => {
    const promos = monetizationData?.promos;
    if (!promos || promos.length === 0) return;
    const currentPromo = promos[currentPromoIndex];
    if (currentPromo) {
      trackImpression(currentPromo.configId);
    }
  }, [currentPromoIndex, monetizationData?.promos, trackImpression]);

  const handleTrackClick = (promoId) => {
    amplifyApi.trackMonetizationEvent(promoId, 'click');
  };

  const progressPercent = totalProducts > 0
    ? Math.max(5, (completedProducts / totalProducts) * 100)
    : 5;

  const promos = monetizationData?.promos || [];
  const currentPromo = promos[currentPromoIndex];
  const adNetwork = monetizationData?.adNetwork;
  const crossSellProducts = monetizationData?.crossSellProducts || [];

  const hasContent = (showPromos && promos.length > 0) ||
    (showAds && adNetwork?.enabled && adNetwork.adUnitId && adNetwork.publisherId) ||
    (showCrossSell && crossSellProducts.length > 0);

  // If monetization is disabled or no content, show simple spinner
  if (!isMonetizationEnabled || (!loading && !hasContent)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6">
        <div className="relative">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${brand.colors.primary}20` }}
          >
            <Loader2 className="w-10 h-10 animate-spin" style={{ color: brand.colors.primary }} />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900">Creating Your Products</h2>
          <p className="text-slate-500 mt-2 animate-pulse">{creationProgress}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Creating {totalProducts} product(s)</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Compact progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: brand.colors.primary }} />
            <span className="text-sm font-medium text-slate-700">Creating Your Products</span>
          </div>
          <span className="text-xs text-slate-500">
            {completedProducts} of {totalProducts}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-2.5 rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: brand.colors.primary
            }}
          />
        </div>
        <p className="text-xs text-slate-500 animate-pulse">{creationProgress}</p>
      </div>

      {/* Promo card section */}
      {showPromos && promos.length > 0 && currentPromo && (
        <div className="relative">
          <PromoCard
            promo={currentPromo}
            brand={brand}
            onTrackClick={handleTrackClick}
          />
          {promos.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-2">
              <button
                onClick={() => setCurrentPromoIndex(prev => (prev - 1 + promos.length) % promos.length)}
                className="p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {promos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPromoIndex(idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    idx === currentPromoIndex ? 'bg-slate-600' : 'bg-slate-300'
                  }`}
                />
              ))}
              <button
                onClick={() => setCurrentPromoIndex(prev => (prev + 1) % promos.length)}
                className="p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-400"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Ad unit section */}
      {showAds && adNetwork?.enabled && adNetwork.adUnitId && (
        <AdUnit
          adUnitId={adNetwork.adUnitId}
          publisherId={adNetwork.publisherId}
        />
      )}

      {/* Cross-sell section */}
      {showCrossSell && crossSellProducts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-medium text-slate-500">You might also like</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {crossSellProducts.map(product => (
              <CrossSellCard
                key={product.id}
                product={product}
                brand={brand}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreationWaitScreen;
